import React, { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Check, RotateCcw, X, ZoomIn } from 'lucide-react';
import { getCroppedImageFile } from './kycCropUtils';

/** CNIC / ID card aspect ≈ 85.6 × 54 mm */
const ID_CARD_ASPECT = 85.6 / 54;

export default function KycImageCropModal({
  open,
  imageSrc,
  title = 'Crop document',
  aspect = ID_CARD_ASPECT,
  fileName = 'kyc-cropped.jpg',
  onCancel,
  onApply,
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setBusy(false);
    setError('');

    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onCancel?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, imageSrc, busy, onCancel]);

  const onCropComplete = useCallback((_area, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleApply = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      setError('Adjust the crop area, then apply.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const file = await getCroppedImageFile(imageSrc, croppedAreaPixels, {
        fileName,
        mimeType: 'image/jpeg',
        quality: 0.92,
      });
      onApply?.(file);
    } catch (err) {
      setError(err?.message || 'Could not crop image. Please try again.');
      setBusy(false);
    }
  };

  if (!open || !imageSrc) return null;

  return (
    <div className="kyc-crop-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="kyc-crop-modal">
        <header className="kyc-crop-modal-header">
          <div>
            <h3>{title}</h3>
            <p className="kyc-hint">
              Drag to reposition. Use zoom so the full ID card fills the frame clearly.
            </p>
          </div>
          <button
            type="button"
            className="kyc-crop-icon-btn"
            onClick={onCancel}
            disabled={busy}
            aria-label="Close crop"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </header>

        <div className="kyc-crop-stage">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
            showGrid
          />
        </div>

        <div className="kyc-crop-controls">
          <label className="kyc-crop-zoom" htmlFor="kyc-crop-zoom">
            <ZoomIn size={16} strokeWidth={2} aria-hidden />
            Zoom
          </label>
          <input
            id="kyc-crop-zoom"
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            disabled={busy}
          />
          <button
            type="button"
            className="kyc-btn kyc-btn--ghost kyc-btn--sm"
            onClick={() => {
              setCrop({ x: 0, y: 0 });
              setZoom(1);
            }}
            disabled={busy}
          >
            <RotateCcw size={14} strokeWidth={2} aria-hidden />
            Reset
          </button>
        </div>

        {error ? (
          <div className="kyc-crop-error" role="alert">
            {error}
          </div>
        ) : null}

        <footer className="kyc-crop-actions">
          <button type="button" className="kyc-btn kyc-btn--ghost kyc-btn--sm" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="kyc-btn kyc-btn--primary kyc-btn--sm" onClick={handleApply} disabled={busy}>
            <Check size={14} strokeWidth={2} aria-hidden />
            {busy ? 'Applying…' : 'Apply crop'}
          </button>
        </footer>
      </div>
    </div>
  );
}

export { ID_CARD_ASPECT };
